import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import studyService from '../../services/studyService';
import surveyService from '../../services/surveyService';
import siteService from '../../services/siteService';
import permissionsService from '../../services/permissionsService';
import { Book, Users, FileText, User, Loader, AlertTriangle, ChevronRight, BarChart2 } from 'lucide-react';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    studiesCount: 0,
    sitesCount: 0,
    surveysCount: 0,
    participantsCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canAccessStudyBuilder, setCanAccessStudyBuilder] = useState(false);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const [studiesData, surveysData, sitesData] = await Promise.all([
          studyService.getStudies(),
          surveyService.getAllSurveys(),
          siteService.getAllSites(),
        ]);

        setStats({
          studiesCount: Array.isArray(studiesData) ? studiesData.length : 0,
          sitesCount: Array.isArray(sitesData.sites) ? sitesData.sites.length : 0,
          surveysCount: Array.isArray(surveysData) ? surveysData.length : 0,
          participantsCount: Array.isArray(sitesData.sites)
            ? sitesData.sites.reduce((acc, site) => acc + (site.total_participants_count || 0), 0)
            : 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('An error occurred while fetching dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Check for study builder permission when component mounts
  useEffect(() => {
    const checkPermission = async () => {
      try {
        if (user) {
          const hasPermission = await permissionsService.hasPermission(
            permissionsService.PERMISSIONS.ACCESS_STUDY_BUILDER
          );
          console.log('[Dashboard] User has study builder access:', hasPermission);
          setCanAccessStudyBuilder(hasPermission);
        }
      } catch (error) {
        console.error('Error checking study builder permission:', error);
        setCanAccessStudyBuilder(false);
      }
    };

    checkPermission();
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, linkTo }) => (
    <Link to={linkTo}>
      <motion.div
        whileHover={{ scale: 1.03 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/20 overflow-hidden transition-all duration-300 group"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-primary-50 p-3 rounded-full">
              <Icon className="w-6 h-6 text-primary-600" />
            </div>
            <ChevronRight className="w-5 h-5 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-1">{value}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-1 w-0 group-hover:w-full transition-all duration-300 ease-in-out"></div>
      </motion.div>
    </Link>
  );

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-12"
      >
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">Welcome back, {user?.first_name || 'User'}</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">Here's an overview of your FlexCOA data</p>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border-l-4 border-red-400 p-4 mb-8 rounded-md"
          >
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <StatCard title="Studies" value={stats.studiesCount} icon={Book} linkTo="/studies" />
        <StatCard title="Sites" value={stats.sitesCount} icon={Users} linkTo="/sites" />
        <StatCard title="Surveys" value={stats.surveysCount} icon={FileText} linkTo="/surveys" />
        <StatCard title="Participants" value={stats.participantsCount} icon={User} linkTo="/participants" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-12"
      >
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            // Only show Create New Study button if user has permission
            ...(canAccessStudyBuilder ? [{ title: "Create New Study", icon: Book, link: "/create-new-study" }] : []),
            /* { title: "Add Site", icon: Users, link: "/add-site" }, */
            { title: "Design Survey", icon: FileText, link: "/surveys" },
          ].map((action, index) => (
            <Link key={index} to={action.link}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 overflow-hidden transition-all duration-300 group"
              >
                <div className="p-6 flex items-center">
                  <div className="bg-primary-50 p-3 rounded-full mr-4">
                    <action.icon className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 group-hover:text-primary-600 transition-colors duration-300">
                    {action.title}
                  </h3>
                  <ChevronRight className="w-5 h-5 text-primary-500 ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-12"
      >
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Data Visualization</h2>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/20 overflow-hidden p-6">
          <BarChart2 className="w-12 h-12 text-primary-600 mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Interactive Charts</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Explore your study data with interactive charts and graphs.</p>
          <Link to="/data-visualization" className="text-primary-600 hover:text-primary-700 font-medium">
            View Charts <ChevronRight className="inline w-4 h-4" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;